import { useState, useEffect } from 'react';
import { X, Calendar, MapPin, User, FileText, Users, UserPlus, Pencil, Eye, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Cirugia,
  getCirugia,
  getColaboradoresDeCirugia,
  updateCirugia,
  getTipoCirugia,
  getHospital,
  getColaboradores,
  getEspecialidades,
  removeColaboradorDeCirugia,
  updateColaboradorDeCirugia,
} from '../../services/cirugiasService';
import AgregarColaboradorModal from './AgregarColaboradorModal';
import FormCirugiaModal from './FormCirugiaModal';
import medicoVfpService, { PacienteVfp } from '../../services/medicoVfpService';

interface Props {
  cirugia: Cirugia;
  onClose: () => void;
  onUpdate: () => Promise<void> | void;
}

export default function DetalleCirugiaModal({ cirugia, onClose, onUpdate }: Props) {
  const [colaboradores, setColaboradores] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAgregarColaborador, setShowAgregarColaborador] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [paciente, setPaciente] = useState<PacienteVfp | null>(null);
  const [pacienteLoading, setPacienteLoading] = useState(false);
  const [tipoCirugia, setTipoCirugia] = useState<any | null>(null);
  const [tipoLoading, setTipoLoading] = useState(false);
  const [hospital, setHospital] = useState<any | null>(null);
  const [hospitalLoading, setHospitalLoading] = useState(false);
  const [colaboradoresCatalog, setColaboradoresCatalog] = useState<Record<number, any>>({});
  const [especialidadesMap, setEspecialidadesMap] = useState<Record<number, string>>({});
  const [notasColaborador, setNotasColaborador] = useState<{ nombre: string; notas?: string; colaboradorId?: number } | null>(null);
  const [updatingColaboradorId, setUpdatingColaboradorId] = useState<number | null>(null);
  const [cirugiaActual, setCirugiaActual] = useState<Cirugia>(cirugia);
  const [editingNotas, setEditingNotas] = useState(false);
  const [notasTemp, setNotasTemp] = useState('');

  useEffect(() => {
    const loadCirugiaCompleta = async () => {
      try {
        const data = await getCirugia(cirugia.id);
        const cirugiaData = data?.data || data;
        setCirugiaActual(cirugiaData);
      } catch (error) {
        console.error('Error al cargar cirugía completa:', error);
        setCirugiaActual(cirugia);
      }
    };
    loadCirugiaCompleta();
  }, [cirugia.id]);

  useEffect(() => {
    loadColaboradores();
  }, [cirugia.id]);

  useEffect(() => {
    loadCatalogosColaboradores();
  }, []);

  useEffect(() => {
    const loadPaciente = async () => {
      if (!cirugia.pacienteId) {
        setPaciente(null);
        return;
      }
      try {
        setPacienteLoading(true);
        const data = await medicoVfpService.getPacienteVfp(cirugia.pacienteId);
        setPaciente(data);
      } catch (error) {
        console.error('Error al cargar paciente:', error);
        setPaciente(null);
      } finally {
        setPacienteLoading(false);
      }
    };

    loadPaciente();
  }, [cirugia.pacienteId]);

  useEffect(() => {
    const loadTipo = async () => {
      if (!cirugia.tipoCirugiaId) {
        setTipoCirugia(null);
        return;
      }
      try {
        setTipoLoading(true);
        const data = await getTipoCirugia(cirugia.tipoCirugiaId);
        setTipoCirugia(data);
      } catch (error) {
        console.error('Error al cargar tipo de cirugía:', error);
        setTipoCirugia(null);
      } finally {
        setTipoLoading(false);
      }
    };

    loadTipo();
  }, [cirugia.tipoCirugiaId]);

  useEffect(() => {
    const loadHospital = async () => {
      if (!cirugia.hospitalId) {
        setHospital(null);
        return;
      }
      try {
        setHospitalLoading(true);
        const data = await getHospital(cirugia.hospitalId);
        setHospital(data);
      } catch (error) {
        console.error('Error al cargar hospital:', error);
        setHospital(null);
      } finally {
        setHospitalLoading(false);
      }
    };

    loadHospital();
  }, [cirugia.hospitalId]);

  const loadColaboradores = async () => {
    try {
      const data = await getColaboradoresDeCirugia(cirugia.id);
      setColaboradores(data);
    } catch (error) {
      console.error('Error al cargar colaboradores:', error);
    }
  };

  const loadCatalogosColaboradores = async () => {
    try {
      const [colabsRes, especialidadesRes] = await Promise.all([
        getColaboradores({ isActive: true, take: 500 }),
        getEspecialidades({ isActive: true, take: 500 }),
      ]);

      const colaboradoresList = Array.isArray(colabsRes)
        ? colabsRes
        : Array.isArray(colabsRes?.data)
        ? colabsRes.data
        : (colabsRes?.data?.data || []);

      const especialidadesList = Array.isArray(especialidadesRes)
        ? especialidadesRes
        : Array.isArray(especialidadesRes?.data)
        ? especialidadesRes.data
        : (especialidadesRes?.data?.data || []);

      const colabMap: Record<number, any> = {};
      colaboradoresList.forEach((colab: any) => {
        if (colab?.id) {
          colabMap[colab.id] = colab;
        }
      });

      const espMap: Record<number, string> = {};
      especialidadesList.forEach((esp: any) => {
        if (esp?.id) {
          espMap[esp.id] = esp.nombre;
        }
      });

      setColaboradoresCatalog(colabMap);
      setEspecialidadesMap(espMap);
    } catch (error) {
      console.error('Error al cargar catálogos de colaboradores:', error);
    }
  };

  const getNombrePaciente = () => {
    if (!paciente) return 'Paciente no disponible';
    return `${paciente.nombre || ''} ${paciente.apellido1 || ''} ${paciente.apellido2 || ''}`.trim() ||
      `Paciente #${paciente.clave}`;
  };

  const getColaboradorDisplay = (colab: any) => {
    const info = colaboradoresCatalog[colab.colaboradorId];
    const nombre = info
      ? `${info.nombre || ''} ${info.apellidos || ''}`.trim() || `Colaborador #${colab.colaboradorId}`
      : `Colaborador #${colab.colaboradorId}`;
    const especialidad = info?.especialidadId
      ? especialidadesMap[info.especialidadId] || 'Sin especialidad'
      : 'Sin especialidad';
    return { nombre, especialidad };
  };

  const handleViewNotas = (colab: any) => {
    const { nombre } = getColaboradorDisplay(colab);
    setNotasColaborador({ nombre, notas: colab.notas, colaboradorId: colab.colaboradorId });
    setNotasTemp(colab.notas || '');
    setEditingNotas(false);
  };

  const handleSaveNotas = async () => {
    if (!notasColaborador?.colaboradorId) return;
    try {
      setUpdatingColaboradorId(notasColaborador.colaboradorId);
      await updateColaboradorDeCirugia(cirugiaActual.id, notasColaborador.colaboradorId, {
        notas: notasTemp || undefined,
      });
      toast.success('Notas actualizadas exitosamente');
      setNotasColaborador(null);
      loadColaboradores();
    } catch (error) {
      console.error('Error al actualizar notas:', error);
      toast.error('Error al actualizar notas');
    } finally {
      setUpdatingColaboradorId(null);
    }
  };

  const handleRemoveColaborador = async (colab: any) => {
    const { nombre } = getColaboradorDisplay(colab);
    if (!confirm(`¿Deseas quitar a ${nombre} de esta cirugía?`)) {
      return;
    }
    try {
      await removeColaboradorDeCirugia(cirugia.id, colab.colaboradorId);
      toast.success('Colaborador eliminado');
      loadColaboradores();
    } catch (error) {
      console.error('Error al eliminar colaborador:', error);
      toast.error('No se pudo eliminar el colaborador');
    }
  };

  const handleToggleConfirm = async (colab: any) => {
    try {
      setUpdatingColaboradorId(colab.colaboradorId);
      await updateColaboradorDeCirugia(cirugia.id, colab.colaboradorId, {
        confirmado: !colab.confirmado,
      });
      toast.success(`Colaborador ${!colab.confirmado ? 'confirmado' : 'marcado como pendiente'}`);
      loadColaboradores();
    } catch (error) {
      console.error('Error al actualizar colaborador:', error);
      toast.error('No se pudo actualizar el estado del colaborador');
    } finally {
      setUpdatingColaboradorId(null);
    }
  };

  const formatFecha = (fecha: string) => {
    const [year, month, day] = fecha.split('T')[0].split('-').map(Number);
    const parts = new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).formatToParts(new Date(year, month - 1, day));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const mon = get('month').replace('.', '').toUpperCase();
    return `${get('day')} ${mon} ${get('year')}`;
  };

  const getEstadoBadgeColor = (estado: string) => {
    switch (estado) {
      case 'prospecto':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmada':
        return 'bg-green-100 text-green-800';
      case 'en_proceso':
        return 'bg-blue-100 text-blue-800';
      case 'concluida':
        return 'bg-gray-100 text-gray-800';
      case 'cancelada':
        return 'bg-red-100 text-red-800';
      case 'pospuesta':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleChangeEstado = async (nuevoEstado: string) => {
    try {
      setLoading(true);
      await updateCirugia(cirugia.id, { estado: nuevoEstado });
      toast.success('Estado actualizado exitosamente');
      await onUpdate();
      onClose();
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      toast.error('Error al actualizar estado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">Detalles de Cirugía</h2>
            <p className="text-sm text-gray-600 mt-1">ID: {cirugiaActual.id}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Estado y Acciones */}
          <div className="flex items-center justify-between">
            <span
              className={`px-4 py-2 rounded-full text-sm font-medium ${getEstadoBadgeColor(
                cirugiaActual.estado
              )}`}
            >
              {cirugiaActual.estado.toUpperCase()}
            </span>
            <div className="flex gap-2 flex-wrap justify-end">
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
              >
                <Pencil className="w-4 h-4" />
                Editar
              </button>
              {cirugiaActual.estado !== 'concluida' && (
                <button
                  onClick={() => handleChangeEstado('concluida')}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  Marcar como Concluida
                </button>
              )}
              {cirugiaActual.estado === 'prospecto' && (
                <button
                  onClick={() => handleChangeEstado('confirmada')}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Confirmar
                </button>
              )}
            </div>
          </div>

          {/* Paciente */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Paciente
            </h3>
            {pacienteLoading ? (
              <p className="text-sm text-gray-500">Cargando información del paciente...</p>
            ) : paciente ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Nombre</label>
                  <p className="text-gray-900 mt-1">{getNombrePaciente()}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Clave</label>
                  <p className="text-gray-900 mt-1">{paciente.clave}</p>
                </div>
                {paciente.medicoVfpPaciDat?.tel1 && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Teléfono</label>
                    <p className="text-gray-900 mt-1">{paciente.medicoVfpPaciDat.tel1}</p>
                  </div>
                )}
                {paciente.medicoVfpPaciDat?.direccion && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Dirección</label>
                    <p className="text-gray-900 mt-1">{paciente.medicoVfpPaciDat.direccion}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No se pudo cargar la información del paciente.</p>
            )}
          </div>

          {/* Información General */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Información General
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Tipo de Cirugía</label>
                {tipoLoading ? (
                  <p className="text-sm text-gray-500 mt-1">Cargando tipo de cirugía...</p>
                ) : tipoCirugia ? (
                  <p className="text-gray-900 mt-1">
                    {tipoCirugia.nombre}
                    {tipoCirugia.categoria && (
                      <span className="block text-sm text-gray-500">{tipoCirugia.categoria}</span>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">No disponible</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Padecimiento</label>
                <p className="text-gray-900 mt-1">{cirugiaActual.padecimiento}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Tipo de Pago</label>
                <p className="text-gray-900 mt-1">
                  {cirugiaActual.tipoPago === 'seguro' ? 'Con Seguro' : 'Sin Seguro'}
                </p>
              </div>
              {cirugiaActual.tipoPago === 'seguro' && cirugiaActual.datosSeguro && (
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-600">Notas del Seguro</label>
                  <div className="mt-1 p-3 bg-white rounded-lg border border-blue-100 text-gray-900 whitespace-pre-line">
                    {cirugiaActual.datosSeguro}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fecha y Hora */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Fecha y Hora
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Fecha Programada</label>
                <p className="text-gray-900 mt-1">{formatFecha(cirugiaActual.fechaProgramada)}</p>
              </div>
              {cirugiaActual.fechaRealizada && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Fecha Realizada</label>
                  <p className="text-gray-900 mt-1">{formatFecha(cirugiaActual.fechaRealizada)}</p>
                </div>
              )}
              {cirugiaActual.horaInicio && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Hora Inicio</label>
                  <p className="text-gray-900 mt-1">{cirugiaActual.horaInicio}</p>
                </div>
              )}
              {cirugiaActual.horaFin && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Hora Fin</label>
                  <p className="text-gray-900 mt-1">{cirugiaActual.horaFin}</p>
                </div>
              )}
            </div>
          </div>

          {/* Ubicación */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Ubicación
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Hospital</label>
                {hospitalLoading ? (
                  <p className="text-sm text-gray-500 mt-1">Cargando hospital...</p>
                ) : hospital ? (
                  <div className="mt-1">
                    <p className="text-gray-900 font-medium">{hospital.nombre}</p>
                    {hospital.direccion && (
                      <p className="text-sm text-gray-600">{hospital.direccion}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">No disponible</p>
                )}
              </div>

              {cirugiaActual.salaQuirurgica && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Sala Quirúrgica</label>
                  <p className="text-gray-900 mt-1">{cirugiaActual.salaQuirurgica}</p>
                </div>
              )}
              {cirugiaActual.numeroExpedienteHospital && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Expediente Hospital</label>
                  <p className="text-gray-900 mt-1">{cirugiaActual.numeroExpedienteHospital}</p>
                </div>
              )}
            </div>
          </div>

          {/* Información Médica */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Información Médica
            </h3>
            <div className="space-y-4">
              {cirugiaActual.diagnosticoPreoperatorio && (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Diagnóstico Preoperatorio
                  </label>
                  <p className="text-gray-900 mt-1">{cirugiaActual.diagnosticoPreoperatorio}</p>
                </div>
              )}
              {cirugiaActual.diagnosticoPostoperatorio && (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Diagnóstico Postoperatorio
                  </label>
                  <p className="text-gray-900 mt-1">{cirugiaActual.diagnosticoPostoperatorio}</p>
                </div>
              )}
              {cirugiaActual.tipoAnestesia && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Tipo de Anestesia</label>
                  <p className="text-gray-900 mt-1">{cirugiaActual.tipoAnestesia}</p>
                </div>
              )}
              {cirugiaActual.riesgoQuirurgico && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Riesgo Quirúrgico</label>
                  <p className="text-gray-900 mt-1">{cirugiaActual.riesgoQuirurgico}</p>
                </div>
              )}
            </div>
          </div>

          {/* Colaboradores */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" />
                Equipo Quirúrgico ({colaboradores.length})
              </h3>
              <button
                onClick={() => setShowAgregarColaborador(true)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <UserPlus className="w-4 h-4" />
                Agregar Colaborador
              </button>
            </div>
            
            {colaboradores.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No hay colaboradores asignados. Click en "Agregar Colaborador" para asignar personal médico.
              </p>
            ) : (
              <div className="space-y-2">
                {colaboradores.map((colab) => {
                  const { nombre, especialidad } = getColaboradorDisplay(colab);
                  return (
                    <div
                      key={colab.id}
                      className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 bg-white rounded-lg border border-gray-200"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{nombre}</p>
                        <p className="text-sm text-gray-600">{especialidad}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Rol: {colab.rolEnCirugia || 'No definido'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            colab.confirmado
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {colab.confirmado ? 'Confirmado' : 'Pendiente'}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleToggleConfirm(colab)}
                          disabled={updatingColaboradorId === colab.colaboradorId}
                          className={`px-3 py-1 text-xs font-medium rounded-lg border transition-colors ${
                            colab.confirmado
                              ? 'border-yellow-300 text-yellow-800 hover:bg-yellow-50'
                              : 'border-green-300 text-green-800 hover:bg-green-50'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          title={colab.confirmado ? 'Marcar como pendiente' : 'Marcar como confirmado'}
                        >
                          {colab.confirmado ? 'Marcar pendiente' : 'Marcar confirmado'}
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewNotas(colab)}
                            className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                            title="Ver notas"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveColaborador(colab)}
                            className="p-2 rounded-full hover:bg-red-50 text-red-600"
                            title="Quitar colaborador"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Notas y Observaciones */}
          {(cirugiaActual.notasPreoperatorias ||
            cirugiaActual.notasPostoperatorias ||
            cirugiaActual.complicaciones ||
            cirugiaActual.observaciones) && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-semibold mb-4">Notas y Observaciones</h3>
              <div className="space-y-4">
                {cirugiaActual.notasPreoperatorias && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Notas Preoperatorias
                    </label>
                    <p className="text-gray-900 mt-1 whitespace-pre-wrap">
                      {cirugiaActual.notasPreoperatorias}
                    </p>
                  </div>
                )}
                {cirugiaActual.notasPostoperatorias && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Notas Postoperatorias
                    </label>
                    <p className="text-gray-900 mt-1 whitespace-pre-wrap">
                      {cirugiaActual.notasPostoperatorias}
                    </p>
                  </div>
                )}
                {cirugiaActual.complicaciones && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Complicaciones</label>
                    <p className="text-gray-900 mt-1 whitespace-pre-wrap">
                      {cirugiaActual.complicaciones}
                    </p>
                  </div>
                )}
                {cirugiaActual.observaciones && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Observaciones</label>
                    <p className="text-gray-900 mt-1 whitespace-pre-wrap">
                      {cirugiaActual.observaciones}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>

      {/* Modal Agregar Colaborador */}
      {showAgregarColaborador && (
        <AgregarColaboradorModal
          cirugiaId={cirugia.id}
          onClose={() => setShowAgregarColaborador(false)}
          onSuccess={() => {
            setShowAgregarColaborador(false);
            loadColaboradores();
          }}
        />
      )}

      {notasColaborador && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingNotas ? 'Editar notas' : 'Notas del colaborador'}
                </h3>
                <p className="text-sm text-gray-600">{notasColaborador.nombre}</p>
              </div>
              <button
                onClick={() => setNotasColaborador(null)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              {editingNotas ? (
                <textarea
                  value={notasTemp}
                  onChange={(e) => setNotasTemp(e.target.value)}
                  placeholder="Escribe las notas del colaborador..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={5}
                />
              ) : (
                <>
                  {notasColaborador.notas ? (
                    <p className="text-gray-800 whitespace-pre-wrap">{notasColaborador.notas}</p>
                  ) : (
                    <p className="text-gray-500 text-sm">Sin notas registradas para este colaborador.</p>
                  )}
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              {editingNotas ? (
                <>
                  <button
                    onClick={() => {
                      setEditingNotas(false);
                      setNotasTemp(notasColaborador.notas || '');
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveNotas}
                    disabled={updatingColaboradorId === notasColaborador.colaboradorId}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    Guardar
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setEditingNotas(true)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                  >
                    Editar
                  </button>
                  <button
                    onClick={() => setNotasColaborador(null)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Cerrar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <FormCirugiaModal
          cirugia={cirugiaActual}
          onClose={() => setShowEditModal(false)}
          onSuccess={async () => {
            setShowEditModal(false);
            await onUpdate();
            onClose();
          }}
        />
      )}
    </div>
  );
}
